import React from 'react';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { 
  FaUser, 
  FaCalendarAlt, 
  FaMoneyBillWave, 
  FaEdit,
  FaPaperPlane,
  FaIdCard,
  FaTag,
  FaStickyNote,
  FaDollarSign,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaCreditCard,
  FaReceipt,
  FaTimes,
  FaPauseCircle,
  FaAdjust,
  FaUserMd,
  FaFileMedical
} from 'react-icons/fa';
import formatDate from '../../../../utils/formatDate';
import Txt from "../../../Text";
import { arClass } from '../../../../utils/useArClass';
import textTranslator from '../../../../utils/useTextTranslator';
import typeTranslator from '../../../../utils/useTypeTranslator';

const Text = ({ children, className, page = "PAYMENTS" }) => {
  return (
    <Txt page={page} className={className} keyName={page}>
      {children}
    </Txt>
  );
};

const Review = ({ payment, selectedPatient, selectedVisit, onEdite, onSubmit }) => {
  const darkMode = useSelector((state) => state.settings.darkMode);

  const getStatusConfig = (status) => {
    const configs = {
      pending: {
        icon: FaClock,
        color: darkMode ? "text-yellow-400" : "text-yellow-600",
        bg: darkMode ? "bg-yellow-900/30" : "bg-yellow-100",
        border: darkMode ? "border-yellow-700/50" : "border-yellow-200",
        iconBg: darkMode ? "bg-yellow-600" : "bg-yellow-500",
        accent: darkMode ? "text-yellow-400" : "text-yellow-600",
        text: darkMode ? "text-yellow-300" : "text-yellow-700",
      },
      completed: {
        icon: FaCheckCircle,
        color: darkMode ? "text-green-400" : "text-green-600",
        bg: darkMode ? "bg-green-900/30" : "bg-green-100",
        border: darkMode ? "border-green-700/50" : "border-green-200",
        iconBg: darkMode ? "bg-green-600" : "bg-green-500",
        accent: darkMode ? "text-green-400" : "text-green-600",
        text: darkMode ? "text-green-300" : "text-green-700",
      },
      failed: {
        icon: FaExclamationTriangle,
        color: darkMode ? "text-red-400" : "text-red-600",
        bg: darkMode ? "bg-red-900/30" : "bg-red-100",
        border: darkMode ? "border-red-700/50" : "border-red-200",
        iconBg: darkMode ? "bg-red-600" : "bg-red-500",
        accent: darkMode ? "text-red-400" : "text-red-600",
        text: darkMode ? "text-red-300" : "text-red-700",
      },
      refunded: {
        icon: FaTimes,
        color: darkMode ? "text-purple-400" : "text-purple-600",
        bg: darkMode ? "bg-purple-900/30" : "bg-purple-100",
        border: darkMode ? "border-purple-700/50" : "border-purple-200",
        iconBg: darkMode ? "bg-purple-600" : "bg-purple-500",
        accent: darkMode ? "text-purple-400" : "text-purple-600",
        text: darkMode ? "text-purple-300" : "text-purple-700",
      },
      cancelled: {
        icon: FaTimes,
        color: darkMode ? "text-red-400" : "text-red-600",
        bg: darkMode ? "bg-red-900/30" : "bg-red-100",
        border: darkMode ? "border-red-700/50" : "border-red-200",
        iconBg: darkMode ? "bg-red-600" : "bg-red-500",
        accent: darkMode ? "text-red-400" : "text-red-600",
        text: darkMode ? "text-red-300" : "text-red-700",
      },
      "on-hold": {
        icon: FaPauseCircle,
        color: darkMode ? "text-yellow-400" : "text-yellow-600",
        bg: darkMode ? "bg-yellow-900/30" : "bg-yellow-100",
        border: darkMode ? "border-yellow-700/50" : "border-yellow-200",
        iconBg: darkMode ? "bg-yellow-600" : "bg-yellow-500",
        accent: darkMode ? "text-yellow-400" : "text-yellow-600",
        text: darkMode ? "text-yellow-300" : "text-yellow-700",
      },
      "partially-paid": {
        icon: FaAdjust,
        color: darkMode ? "text-blue-400" : "text-blue-600",
        bg: darkMode ? "bg-blue-900/30" : "bg-blue-100",
        border: darkMode ? "border-blue-700/50" : "border-blue-200",
        iconBg: darkMode ? "bg-blue-600" : "bg-blue-500",
        accent: darkMode ? "text-blue-400" : "text-blue-600",
        text: darkMode ? "text-blue-300" : "text-blue-700",
      },
    };
    return configs[payment.paymentStatus?.toLowerCase()] || configs.pending;
  };

  const getMethodConfig = (method) => {
    const configs = {
      cash: {
        icon: FaMoneyBillWave,
        color: darkMode ? "text-green-400" : "text-green-600",
        bg: darkMode ? "bg-green-900/30" : "bg-green-100",
      },
      "credit-card": {
        icon: FaCreditCard,
        color: darkMode ? "text-blue-400" : "text-blue-600",
        bg: darkMode ? "bg-blue-900/30" : "bg-blue-100",
      },
      "debit-card": {
        icon: FaCreditCard,
        color: darkMode ? "text-purple-400" : "text-purple-600",
        bg: darkMode ? "bg-purple-900/30" : "bg-purple-100",
      },
      insurance: {
        icon: FaUserMd,
        color: darkMode ? "text-orange-400" : "text-orange-600",
        bg: darkMode ? "bg-orange-900/30" : "bg-orange-100",
      },
      "online-payment": {
        icon: FaReceipt,
        color: darkMode ? "text-cyan-400" : "text-cyan-600",
        bg: darkMode ? "bg-cyan-900/30" : "bg-cyan-100",
      },
      other: {
        icon: FaFileMedical,
        color: darkMode ? "text-gray-400" : "text-gray-600",
        bg: darkMode ? "bg-gray-900/30" : "bg-gray-100",
      },
    };
    return configs[payment.method?.toLowerCase()] || configs.cash;
  };

  const statusConfig = getStatusConfig(payment.paymentStatus);
  const StatusIcon = statusConfig.icon;
  const methodConfig = getMethodConfig(payment.method);
  const MethodIcon = methodConfig.icon;

  const amountAfterDiscount = (
    Number(payment.amount || 0) *
    (1 - Number(payment.discountPercent || 0) / 100)
  ).toFixed(2);

  return (
    <div className={arClass("space-y-6 max-sm:space-y-4")}>
      

      {/* Main Review Grid */}
      <div className={arClass("flex gap-2 max-lg:flex-col w-full items-stretch")}>
        {/* Left Column - Patient & Visit Info */}
        <div className={arClass("flex flex-col gap-2 flex-1")}>
          {/* Patient Information Card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="group flex-1"
          >
            <div
              className={arClass(`rounded-2xl p-6 max-sm:p-4 max-sm:rounded-xl transition-all duration-300 overflow-hidden h-full ${
                darkMode
                  ? "bg-slate-800/50 border border-slate-700/50"
                  : "bg-white border border-slate-200"
              }`)}
            >
              <div
                className={arClass(`relative px-6 py-4 border-b max-sm:px-4 max-sm:py-3 ${
                  darkMode
                    ? "border-slate-700/50 bg-blue-900/30"
                    : "border-slate-200 bg-blue-50"
                }`)}
              >
                <div className={arClass("flex items-center gap-4 max-sm:gap-3")}>
                  <motion.div
                    animate={{
                      scale: [1, 1.05, 1],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                    }}
                    className={arClass(`p-3 rounded-xl max-sm:p-2 ${
                      darkMode ? "bg-blue-600" : "bg-blue-500"
                    }`)}
                  >
                    <FaUser className="w-6 h-6 text-white max-sm:w-5 max-sm:h-5" />
                  </motion.div>
                  <div className={arClass("max-sm:flex-1 max-sm:min-w-0")}>
                    <h3
                      className={arClass(`text-xl font-bold max-sm:text-lg ${
                        darkMode ? "text-white" : "text-slate-900"
                      }`)}
                    >
                      <Text>Patient Information</Text>
                    </h3>
                    <p
                      className={arClass(`text-sm max-sm:text-xs ${
                        darkMode ? "text-blue-300" : "text-blue-600"
                      }`)}
                    >
                      <Text page='PATIENTS'>Selected patient details</Text>
                    </p>
                  </div>
                </div>
              </div>

              <div className={arClass("p-6 max-sm:p-4")}>
                <div className={arClass("space-y-4 max-sm:space-y-3")}>
                  <div className={arClass("flex items-center justify-between")}>
                    <div className={arClass("flex items-center gap-3")}>
                      <FaUser className={arClass(`w-5 h-5 ${darkMode ? "text-blue-400" : "text-blue-600"}`)} />
                      <span className={arClass(`text-sm ${darkMode ? "text-slate-300" : "text-slate-700"}`)}>
                        <Text page='PATIENTS'>Full Name</Text>
                      </span>
                    </div>
                    <span className={arClass(`font-medium ${darkMode ? "text-white" : "text-slate-900"}`)}>
                      {selectedPatient?.firstName} {selectedPatient?.lastName}
                    </span>
                  </div>

                  <div className={arClass("flex items-center justify-between")}>
                    <div className={arClass("flex items-center gap-3")}>
                      <FaIdCard className={arClass(`w-5 h-5 ${darkMode ? "text-blue-400" : "text-blue-600"}`)} />
                      <span className={arClass(`text-sm ${darkMode ? "text-slate-300" : "text-slate-700"}`)}>
                        <Text>Patient ID</Text>
                      </span>
                    </div>
                    <span className={arClass(`font-mono font-medium ${darkMode ? "text-white" : "text-slate-900"}`)}>
                      {selectedPatient?.id}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Visit Information Card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="group flex-1"
          >
            <div
              className={arClass(`rounded-2xl p-6 max-sm:p-4 max-sm:rounded-xl transition-all duration-300 overflow-hidden h-full ${
                darkMode
                  ? "bg-slate-800/50 border border-slate-700/50"
                  : "bg-white border border-slate-200"
              }`)}
            >
              <div
                className={arClass(`relative px-6 py-4 border-b max-sm:px-4 max-sm:py-3 ${
                  darkMode
                    ? "border-slate-700/50 bg-purple-900/30"
                    : "border-slate-200 bg-purple-50"
                }`)}
              >
                <div className={arClass("flex items-center gap-4 max-sm:gap-3")}>
                  <motion.div
                    animate={{
                      scale: [1, 1.05, 1],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                    }}
                    className={arClass(`p-3 rounded-xl max-sm:p-2 ${
                      darkMode ? "bg-purple-600" : "bg-purple-500"
                    }`)}
                  >
                    <FaCalendarAlt className="w-6 h-6 text-white max-sm:w-5 max-sm:h-5" />
                  </motion.div>
                  <div className={arClass("max-sm:flex-1 max-sm:min-w-0")}>
                    <h3
                      className={arClass(`text-xl font-bold max-sm:text-lg ${
                        darkMode ? "text-white" : "text-slate-900"
                      }`)}
                    >
                      <Text page='VISITS'>Visit Information</Text>
                    </h3>
                    <p
                      className={arClass(`text-sm max-sm:text-xs ${
                        darkMode ? "text-purple-300" : "text-purple-600"
                      }`)}
                    >
                      <Text page='VISITS'>Visit details and appointment information</Text>
                    </p>
                  </div>
                </div>
              </div>

              <div className={arClass("p-6 max-sm:p-4")}>
                <div className={arClass("space-y-4 max-sm:space-y-3")}>
                  <div className={arClass("flex items-center justify-between")}>
                    <div className={arClass("flex items-center gap-3")}>
                      <FaCalendarAlt className={arClass(`w-5 h-5 ${darkMode ? "text-purple-400" : "text-purple-600"}`)} />
                      <span className={arClass(`text-sm ${darkMode ? "text-slate-300" : "text-slate-700"}`)}>
                        <Text page='VISITS'>Visit ID</Text>
                      </span>
                    </div>
                    <span className={arClass(`font-mono font-medium ${darkMode ? "text-white" : "text-slate-900"}`)}>
                      #{selectedVisit?.id}
                    </span>
                  </div>

                  <div className={arClass("flex items-center justify-between")}>
                    <div className={arClass("flex items-center gap-3")}>
                      <FaCalendarAlt className={arClass(`w-5 h-5 ${darkMode ? "text-purple-400" : "text-purple-600"}`)} />
                      <span className={arClass(`text-sm ${darkMode ? "text-slate-300" : "text-slate-700"}`)}>
                        <Text page='VISITS'>Date</Text>
                      </span>
                    </div>
                    <span className={arClass(`font-medium ${darkMode ? "text-white" : "text-slate-900"}`)}>
                      {formatDate(selectedVisit?.scheduledAt, false)}
                    </span>
                  </div>
                  

                  <div className={arClass("flex items-center justify-between")}>
                    <div className={arClass("flex items-center gap-3")}>
                      <FaFileMedical className={arClass(`w-5 h-5 ${darkMode ? "text-purple-400" : "text-purple-600"}`)} />
                      <span className={arClass(`text-sm ${darkMode ? "text-slate-300" : "text-slate-700"}`)}>
                        <Text page='VISITS'>Visit Type</Text>
                      </span>
                    </div>
                    <span className={arClass(`font-medium ${darkMode ? "text-white" : "text-slate-900"}`)}>
                      {typeTranslator(selectedVisit?.visitType?.name || "—").translatedType}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right Column - Payment Summary */}
        <div className={arClass(" max-sm:space-y-4 flex-1")}>
          {/* Payment Summary Card */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="group"
          >
            <div
              className={arClass(`rounded-2xl p-6 max-sm:p-4 max-sm:rounded-xl transition-all duration-300 overflow-hidden h-full ${
                darkMode
                  ? "bg-slate-800/50 border border-slate-700/50"
                  : "bg-white border border-slate-200"
              }`)}
            >
              <div
                className={arClass(`relative px-6 py-4 border-b max-sm:px-4 max-sm:py-3  ${
                  darkMode
                    ? "border-slate-700/50 bg-green-900/30"
                    : "border-slate-200 bg-green-50"
                }`)}
              >
                <div className={arClass("flex items-center gap-4 max-sm:gap-3")}>
                  <motion.div
                    animate={{
                      scale: [1, 1.05, 1],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    className={arClass(`p-3 rounded-xl max-sm:p-2 ${
                      darkMode ? "bg-green-600" : "bg-green-500"
                    } shadow-sm`)}
                  >
                    <FaReceipt className="w-6 h-6 text-white max-sm:w-5 max-sm:h-5" />
                  </motion.div>
                  <div className={arClass("max-sm:flex-1 max-sm:min-w-0")}>
                    <h3
                      className={arClass(`text-xl font-bold max-sm:text-lg ${
                        darkMode ? "text-white" : "text-slate-900"
                      }`)}
                    >
                      <Text>Payment Summary</Text>
                    </h3>
                    <p
                      className={arClass(`text-sm max-sm:text-xs ${
                        darkMode ? "text-green-300" : "text-green-600"
                      }`)}
                    >
                      <Text>Complete payment breakdown and details</Text>
                    </p>
                  </div>
                </div>
              </div>

              <div className={arClass("p-6 max-sm:p-4")}>
                <div className={arClass("space-y-4 max-sm:space-y-3")}>
                  {/* Amount Section */}
                  <div
                    className={arClass(`p-4 rounded-lg max-sm:p-3 ${
                      darkMode ? "bg-slate-800/30" : "bg-slate-50"
                    }`)}
                  >
                    <div className={arClass("flex items-center justify-between mb-3")}>
                      <div className={arClass("flex items-center gap-2")}>
                        <FaDollarSign className={arClass(`w-5 h-5 ${statusConfig.accent}`)} />
                        <span className={arClass(`font-semibold ${darkMode ? "text-white" : "text-slate-900"}`)}>
                          <Text>Amount</Text>
                        </span>
                      </div>
                      <div className={arClass(`text-2xl font-bold max-sm:text-xl ${statusConfig.accent}`)}>
                        ${amountAfterDiscount}
                      </div>
                    </div>
                    <div className={arClass("grid grid-cols-2 gap-3")}>
                      <div className={arClass("space-y-1")}>
                        <div className={arClass(`text-xs ${darkMode ? "text-slate-400" : "text-slate-600"}`)}>
                          <Text>Original Amount</Text>
                        </div>
                        <div className={arClass(`font-medium ${darkMode ? "text-white" : "text-slate-900"}`)}>
                          ${Number(payment.amount || 0).toFixed(2)}
                        </div>
                      </div>
                      <div className={arClass("space-y-1")}>
                        <div className={arClass(`text-xs ${darkMode ? "text-slate-400" : "text-slate-600"}`)}>
                          <Text>Discount</Text>
                        </div>
                        <div className={arClass(`font-medium ${darkMode ? "text-white" : "text-slate-900"}`)}>
                          {payment.discountPercent || 0}%
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Status & Method Section */}
                  <div className={arClass("grid grid-cols-2 gap-4 max-sm:grid-cols-1")}>
                    {/* Status Card */}
                    <div className={arClass(`p-3 rounded-lg ${statusConfig.bg} border ${statusConfig.border}`)}>
                      <div className={arClass("flex items-center gap-2 mb-2")}>
                        <StatusIcon className={arClass(`w-4 h-4 ${statusConfig.accent}`)} />
                        <span className={arClass(`text-xs font-semibold ${darkMode ? "text-white" : "text-slate-900"}`)}>
                          <Text>Payment Status</Text>
                        </span>
                      </div>
                      <div className={arClass(`text-sm font-bold ${statusConfig.text}`)}>
                        {textTranslator(payment.paymentStatus, "PAYMENTS")}
                      </div>
                    </div>

                    {/* Method Card */}
                    <div className={arClass(`p-3 rounded-lg ${methodConfig.bg}`)}>
                      <div className={arClass("flex items-center gap-2 mb-2")}>
                        <MethodIcon className={arClass(`w-4 h-4 ${methodConfig.color}`)} />
                        <span className={arClass(`text-xs font-semibold ${darkMode ? "text-white" : "text-slate-900"}`)}>
                          <Text>Payment Method</Text>
                        </span>
                      </div>
                      <div className={arClass(`text-sm font-bold ${darkMode ? "text-white" : "text-slate-900"}`)}>
                        {textTranslator(payment.method, "PAYMENTS")}
                      </div>
                    </div>
                  </div>

               
                    <div
                      className={arClass(`p-3 rounded-lg max-sm:p-2 ${
                        darkMode ? "bg-slate-800/30" : "bg-slate-50"
                      }`)}
                    >
                      <div className={arClass("flex items-center gap-2 mb-2")}>
                        <FaStickyNote className={arClass(`w-4 h-4 ${darkMode ? "text-slate-400" : "text-slate-600"}`)} />
                        <span className={arClass(`text-xs font-semibold ${darkMode ? "text-white" : "text-slate-900"}`)}>
                          <Text>Notes</Text>
                        </span>
                      </div>
                      <div
                        className={arClass(`text-sm max-sm:text-xs ${darkMode ? "text-slate-300" : "text-slate-700"}`)}
                      >
                        {payment.notes || "—"}
                      </div>
                    </div>
               

                  {/* Ready Indicator */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className={arClass(`p-4 rounded-lg text-center max-sm:p-3 ${
                      darkMode
                        ? "bg-green-900/20 border border-green-700/50"
                        : "bg-green-50 border border-green-200"
                    }`)}
                  >
                    <div className={arClass(`text-sm font-semibold max-sm:text-xs ${darkMode ? "text-green-400" : "text-green-600"}`)}>
                      <Text page="PAYMENTS">Ready to Submit</Text>
                    </div>
                    <div className={arClass(`text-xs mt-1 max-sm:text-xss ${darkMode ? "text-slate-500" : "text-slate-600"}`)}>
                      <Text page="PAYMENTS">All payment details are confirmed and ready</Text>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>

         
        </div>
      </div>
    </div>
  );
}

export default Review;